"use client"

import { ReactNode, useEffect } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { getQueryClient } from '@/lib/query'
import { ClientSelectionProvider } from '@/contexts/client-selection'
import { CopilotContextProvider } from '@/contexts/copilot'
import { ThemeProvider } from 'next-themes'
import { SessionProvider } from 'next-auth/react'
import { usePathname } from 'next/navigation'

export function Providers({ children, session }: { children: ReactNode; session?: any }) {
  const client = getQueryClient()
  const pathname = usePathname()
  const isMarketingPage = pathname === '/'
  const isPublicAuthPage = !!pathname && (
    pathname === '/login' ||
    pathname === '/sign-in' ||
    pathname === '/activate' ||
    pathname === '/reset-password' ||
    pathname.startsWith('/login') ||
    pathname.startsWith('/sign-in')
  )
  const skipSessionProvider = isMarketingPage || isPublicAuthPage
  return (
    <QueryClientProvider client={client}>
      <ClientSelectionProvider>
        <CopilotContextProvider>
          <ThemeProvider
            attribute="class"
            defaultTheme="light"
            enableSystem={false}
            disableTransitionOnChange
            storageKey="app-theme"
            themes={["light","dark"]}
          >
            <ThemeFix />
            {skipSessionProvider ? children : (
              <SessionProvider session={session}>
                {children}
              </SessionProvider>
            )}
          </ThemeProvider>
        </CopilotContextProvider>
      </ClientSelectionProvider>
    </QueryClientProvider>
  )
}

function ThemeFix() {
  useEffect(() => {
    try {
      const d = document.documentElement
      const legacy = localStorage.getItem('theme')
      if (legacy) localStorage.removeItem('theme')
      const current = localStorage.getItem('app-theme')
      if (current === 'dark') {
        d.classList.add('dark')
      } else {
        d.classList.remove('dark')
      }
    } catch {}
  }, [])
  return null
}
